import { useHistory } from "react-router";
import { useState } from "react";
import { Info, TriangleAlert } from "lucide-react";

import {
  Alert,
  AlertAction,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";

import { PermissionsKeys } from "components/permissions/permission-keys";
import { PermissionsReasons } from "components/permissions/permission-messages";
import {
  PermissionsWrapper,
  PermissionTooltipButton,
} from "components/permissions/permissions";
import { OverageField } from "components/settings/shared/overage/overage-field";
import {
  CreditsInfo,
  PaymentMethod,
  Subscription as SubscriptionData,
} from "data/models";
import * as Routes from "routes";
import { formatPrice } from "utils/format";
import { Label } from "@/components/ui/label";

interface OverageCardProps {
  subscription: SubscriptionData;
  credits: CreditsInfo;
  paymentMethod: PaymentMethod | null;
}

export const OverageCard = ({
  subscription,
  credits,
  paymentMethod,
}: OverageCardProps) => {
  const history = useHistory();
  const [isEditing, setIsEditing] = useState(false);

  const active = subscription.activeSubscription;
  if (!active) return null;

  const uncappedOverage =
    credits.isOverageEnabled &&
    (active.isMyob || active.plan.overage_unlimited);

  const overagePct = credits.isOverageEnabled
    ? Math.min(
        (credits.overageDollarsSpent / credits.overageDollarsLimit) * 100,
        100,
      )
    : 0;

  if (uncappedOverage) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Overage</CardTitle>
          <CardDescription>
            Billed at the end of your subscription period
          </CardDescription>
        </CardHeader>
        <PermissionsWrapper
          keys={[PermissionsKeys.overageRead]}
          permissionMessageTemplateFn={(content: string) => (
            <CardContent>
              <Alert variant="warning">
                <TriangleAlert />
                <AlertDescription>{content}</AlertDescription>
              </Alert>
            </CardContent>
          )}
        >
          <CardContent className="flex flex-col gap-4">
            <div>
              <Label className="text-muted-foreground">Overage Rate</Label>
              <p className="text-base">
                ${credits.overageDollarsPerCreditsBlock} per{" "}
                {credits.overageCreditsBlock.toLocaleString()} credits
              </p>
            </div>
            <div>
              <Label className="text-muted-foreground">
                Overage Used This Period
              </Label>
              <p className="text-base">
                {credits.overageCreditsSpent.toLocaleString()} credits (
                {formatPrice(credits.overageDollarsSpent)})
              </p>
            </div>
            <Alert variant="info">
              <Info />
              <AlertDescription>
                Your plan has no overage limit. Any usage beyond your plan
                credits is tracked and billed separately — your apps will keep
                running.
              </AlertDescription>
            </Alert>
          </CardContent>
        </PermissionsWrapper>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Overage</CardTitle>
        <CardDescription>
          Billed at the end of your subscription period
        </CardDescription>
      </CardHeader>

      <PermissionsWrapper
        keys={[PermissionsKeys.overageRead]}
        permissionMessageTemplateFn={(content: string) => (
          <CardContent>
            <Alert variant="warning">
              <TriangleAlert />
              <AlertDescription>{content}</AlertDescription>
            </Alert>
          </CardContent>
        )}
      >
        <CardContent>
          {paymentMethod == null && (
            <PermissionsWrapper
              keys={[PermissionsKeys.paymentMethodUpdate]}
              messages={{
                [PermissionsReasons.ROLE_DENY]: (
                  <Alert variant="warning">
                    <TriangleAlert />
                    <AlertDescription>
                      Payment method must be configured to enable overage.
                    </AlertDescription>
                  </Alert>
                ),
              }}
            >
              <Alert variant="warning">
                <TriangleAlert />
                <AlertTitle>Overage can't be configured</AlertTitle>
                <AlertDescription>
                  Overage can't be configured without a valid payment method
                  attached to your account.
                </AlertDescription>
                <AlertAction>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => history.push(Routes.orgBillingDetails)}
                  >
                    Add Payment Method
                  </Button>
                </AlertAction>
              </Alert>
            </PermissionsWrapper>
          )}

          {paymentMethod !== null && (
            <div className="flex flex-col gap-4">
              <div>
                <Label className="text-muted-foreground">Overage Rate</Label>
                <p className="text-base">
                  {credits.isOverageEnabled
                    ? `$${credits.overageDollarsPerCreditsBlock} per ${credits.overageCreditsBlock} credits.`
                    : "-"}
                </p>
              </div>

              {!isEditing && (
                <>
                  <div>
                    <div className="mb-1 flex items-baseline justify-between">
                      <Label className="text-muted-foreground">
                        Overage Spend
                      </Label>
                      <span className="text-lg whitespace-nowrap">
                        {formatPrice(credits.overageDollarsSpent)} of $
                        {credits.overageDollarsLimit.toLocaleString()}
                      </span>
                    </div>
                    <Progress value={overagePct} className="h-2" />
                    <p className="text-muted-foreground mt-2 text-sm">
                      Dollar value of overage used this period, up to your $
                      {credits.overageDollarsLimit.toLocaleString()} limit.
                    </p>
                  </div>
                  {credits.isOverageEnabled &&
                    credits.overageDollarsSpent >=
                      credits.overageDollarsLimit && (
                      <Alert variant="destructive">
                        <TriangleAlert />
                        <AlertDescription>
                          You've consumed all your set overage up to your limit.
                        </AlertDescription>
                      </Alert>
                    )}
                </>
              )}

              {isEditing && (
                <OverageField
                  currentOverage={credits}
                  plan={active.plan}
                  onCancel={() => setIsEditing(false)}
                  onSubmit={() => setIsEditing(false)}
                />
              )}
            </div>
          )}
        </CardContent>

        {!isEditing && paymentMethod !== null && (
          <CardFooter className="flex justify-end">
            <PermissionTooltipButton
              permissionKeys={[PermissionsKeys.overageUpdate]}
              onClick={() => setIsEditing(true)}
            >
              Edit Limit
            </PermissionTooltipButton>
          </CardFooter>
        )}
      </PermissionsWrapper>
    </Card>
  );
};
